import { AppearanceProvider, getAppearanceMode, subscribeAppearance, useAppearance } from "./components/contexts/AppearanceContext";
import './App.css';
import './utils/notificationDesign.css';
import siteConfig from './siteConfig';
import { ClientReloadGate } from './components/ClientReloadGate';
import { requireClientReload } from './utils/clientReload';
import { watchDeployedVersion } from './utils/deployedVersion';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/contexts/AuthContext'; // Updated import
import { MediaAnalysisProvider } from './features/media-analysis/views/MediaAnalysisProvider';
import { GlobalContext } from './components/contexts/GlobalContext';
import { useCallback, useState, useEffect, useSyncExternalStore, Suspense, lazy } from 'react';
import axios from 'axios';

import { SocketProvider } from './components/contexts/SocketContext'; // Updated path
import BusinessResolutionRecovery from './features/switchover/viewmodels/BusinessResolutionRecovery';
import { WatiSocketProvider } from './components/contexts/WatiSocketContext';
import { CanonicalChatNotifications } from './components/messaging/CanonicalChatNotifications';
import QuarantineNotifications from './features/chat-security/views/QuarantineNotifications';
import { CanonicalMessagingProvider } from './components/contexts/CanonicalMessagingContext';
import AppSplash from './components/AppSplash';
import { App as AntApp, ConfigProvider, theme as antdTheme } from 'antd';
import { appThemeConfig } from './components/theme/AppTheme';
import { registerNotificationDismisser } from './utils/notificationRegistry';
import { APP_BRAND_THEME, brandCssVariables } from './components/theme/brandTheme';
import DrawerNew from './components/DrawerNew';
import GetTitle from './components/GetTitle';
import { handleDismissibleNotificationClick } from './utils/dismissibleNotifications';
import { InternalChatProvider } from './components/internalChat/InternalChatContext';
import { useSocket } from './components/contexts/SocketContext';
import { API_DOMAIN } from './components/constants';
import { isSrmUpdateNewer, srmUpdateRequiresLogout } from './utils/srmVersion';
import { AccessProvider, useAccess } from './features/access/context/AccessProvider';
import RequirePermission from './features/access/components/RequirePermission';
import AccessDenied from './features/access/components/AccessDenied';
import { firstAccessiblePath, PERMISSIONS } from './features/access/constants/permissionKeys';
import { AccountsNotificationsProvider } from './components/payments-v2/contexts/AccountsNotificationsContext';
import { OperationsNotificationsProvider } from './components/operations-v2/contexts/OperationsNotificationsContext';
import { CustomerProfileOverlayProvider } from './components/qv4/views/shared/CustomerProfileOverlayProvider';
import { MailboxProvider } from './components/mailbox/MailboxProvider';
import { SourcingProvider } from './features/sourcing-team/viewmodels/SourcingProvider';
import AutomationNotifications from './features/supplier-enquiries/views/AutomationNotifications';

// Import Pages — Login stays eager (first paint for logged-out users);
// everything else is code-split so the initial bundle stays lean.
import Login from './pages/Login';
import ResetPasswordPage from './features/auth/views/ResetPasswordPage';
import NotFound from './components/Modals/NotFound';
const InternalChatPage = lazy(() => import('./components/internalChat/InternalChatLauncher').then(module => ({ default: module.InternalChatPage })));

// Stable reference — passing a fresh object literal here on every App
// re-render (App re-renders often, since GlobalContext's own value prop is
// also a fresh object every time) made antd's <App> treat its notification
// config as "changed" on every render, tearing down and recreating the
// entire notification holder mid-display. That explained late-appearing,
// almost-instantly-dismissed notifications landing in the wrong corner
// (a freshly recreated holder falling back to its default placement before
// ever applying a call's actual placement).
const ANT_APP_NOTIFICATION_CONFIG = { stack: false };

// Popups (notifications, messages, confirm dialogs) follow the appearance
// setting; page content keeps the brand theme until its screen opts into
// AppTheme.
const APP_CONTENT_THEME = { ...APP_BRAND_THEME, inherit: false, algorithm: antdTheme.defaultAlgorithm };
function StaticPopupTheme({ children }) {
  const mode = useSyncExternalStore(subscribeAppearance, getAppearanceMode);
  return <ConfigProvider theme={appThemeConfig(mode === 'dark')}>{children}</ConfigProvider>;
}
// Static Modal/message/notification APIs render outside the normal React tree.
ConfigProvider.config({
  holderRender: children => <StaticPopupTheme>{children}</StaticPopupTheme>,
});
function PopupTheme({ children }) {
  const { isDark } = useAppearance();
  return <ConfigProvider theme={appThemeConfig(isDark)}>{children}</ConfigProvider>;
}
// Lets logout clear App.useApp()'s notifications and messages.
function AppPopupDismisser() {
  const { notification, message } = AntApp.useApp();
  useEffect(() => registerNotificationDismisser(() => { notification.destroy(); message.destroy(); }), [notification, message]);
  return null;
}
// Text defaults the App wrapper used to give the page (it renders no element now).
function AppTextRoot({ children }) {
  const { token } = antdTheme.useToken();
  return <div style={{ color: token.colorText, fontSize: token.fontSize, lineHeight: token.lineHeight, fontFamily: token.fontFamily }}>{children}</div>;
}
Object.entries(brandCssVariables()).forEach(([name, value]) => document.documentElement.style.setProperty(name, value));

const DashboardV2      = lazy(() => import('./pages/DashboardV2'));
const Users            = lazy(() => import('./pages/Users'));
const QuerysetV4       = lazy(() => import('./components/qv4/views/page/QuerysetV4'));
const OperationsV2     = lazy(() => import('./components/operations-v2/views/page/OperationsV2'));
const MyProcurement    = lazy(() => import('./components/operations-v2/views/my-work/MyProcurementPage'));
const MyLogistics      = lazy(() => import('./components/operations-v2/views/my-work/MyLogisticsPage'));
const PaymentsAccounts = lazy(() => import('./components/payments-v2/views/accounts/PaymentsAccountsPage'));
const MailPage = lazy(() => import('./components/mailbox/MailPage'));
const WhatsApp         = lazy(() => import('./pages/WhatsApp'));
const Supplier         = lazy(() => import('./features/suppliers/views/SuppliersPage'));
const Hrm              = lazy(() => import('./pages/HRM'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const Reports          = lazy(() => import('./pages/Reports'));
const OverviewReport   = lazy(() => import('./pages/Reports/Overview'));
const PriceSearch      = lazy(() => import('./pages/PriceSearch'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const AIAssistant      = lazy(() => import('./pages/AIAssistant'));
const ViewsPreview     = lazy(() => import('./pages/ViewsPreview'));
const EnquiriesPage = lazy(() => import('./features/sourcing-team/views/EnquiriesPage'));

const Protected = ({ permission, children }) => <RequirePermission permission={permission}>{children}</RequirePermission>;
const AccessibleHome = () => {
  const { status, can } = useAccess();
  if (status === 'loading' || status === 'idle') return <AppSplash />;
  return <Navigate to={firstAccessiblePath(can)} replace />;
};


const NewVersionBanner = () => {
  const { socket, isConnected } = useSocket();
  const { logout } = useAuth();
  const [updateInfo, setUpdateInfo] = useState(null);
  const processUpdate = useCallback(data => {
    if (!isSrmUpdateNewer(data, siteConfig)) return;
    setUpdateInfo(data);
    requireClientReload();
  }, []);
  useEffect(() => {
    if (!socket) return undefined;
    socket.on("newVersionAvailable", processUpdate);
    return () => socket.off("newVersionAvailable", processUpdate);
  }, [socket, processUpdate]);
  useEffect(() => {
    if (!isConnected) return;
    axios.get(`${API_DOMAIN}/whatsapp/latest_srm_update`).then(response => processUpdate(response.data?.data)).catch(() => {});
  }, [isConnected, processUpdate]);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;
    return watchDeployedVersion({ current: siteConfig, onNewer: processUpdate });
  }, [processUpdate]);
  return <ClientReloadGate availableVersion={updateInfo} changelog={updateInfo?.changelog} requiresLogout={srmUpdateRequiresLogout(updateInfo)} onReload={() => {
    if (srmUpdateRequiresLogout(updateInfo)) logout();
    window.location.reload();
  }} />;
};

// Create a wrapper component for authenticated routes
const AuthenticatedApp = () => {
  const { authState } = useAuth();

  return (
    <>
      <NewVersionBanner />
      <Routes>
        {/* Public: opened from the password reset email, signed in or not. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {!authState.isAuthenticated ? (
        <Route path="*" element={<Login />} />
      ) : (
        <Route path="/" element={<DrawerNew title={<GetTitle />} />}>
          <Route path="/login" element={<AccessibleHome />} />
          <Route index element={<AccessibleHome />} />
          <Route path="/dashboard" element={<Protected permission={PERMISSIONS.DASHBOARD_VIEW}><DashboardV2 /></Protected>} />
          <Route path="/users" element={<Protected permission={PERMISSIONS.USER_APPROVALS_VIEW}><Users /></Protected>} />
          <Route path="/querysetv4" element={<Protected permission={PERMISSIONS.SALES_VIEW}><QuerysetV4 /></Protected>} />
          <Route path="/querysetv4/active-chats" element={<Protected permission={PERMISSIONS.SALES_VIEW_AGENT_CHAT_OVERVIEW}><QuerysetV4 /></Protected>} />
          <Route path="/operations-v2" element={<Protected permission={PERMISSIONS.OPERATIONS_VIEW}><OperationsV2 /></Protected>} />
          <Route path="/operations-v2/orders/:orderId" element={<Protected permission={PERMISSIONS.OPERATIONS_VIEW}><OperationsV2 /></Protected>} />
          <Route path="/my-purchases" element={<Protected permission={PERMISSIONS.OPERATIONS_RECORD_SUPPLIER_OUTCOME}><MyProcurement /></Protected>} />
          <Route path="/my-deliveries" element={<Protected permission={PERMISSIONS.OPERATIONS_EXECUTE_LOGISTICS}><MyLogistics /></Protected>} />
          <Route path="/payments/accounts" element={<Protected permission={PERMISSIONS.PAYMENTS_VIEW_ACCOUNTS_WORKSPACE}><PaymentsAccounts /></Protected>} />
          <Route path="/mail" element={<Protected permission={PERMISSIONS.MAILBOX_VIEW}><MailPage /></Protected>} />
          <Route path="/internal-chat" element={<Protected permission={PERMISSIONS.INTERNAL_CHAT_USE}><InternalChatPage /></Protected>} />
          <Route path="/mail/:threadId" element={<Protected permission={PERMISSIONS.MAILBOX_VIEW}><MailPage /></Protected>} />
          <Route path="/whatsapp" element={<Protected permission={PERMISSIONS.WATI_DIAGNOSTICS_VIEW}><WhatsApp /></Protected>} />
          {/* Supplier chats moved into the sales pipeline's Suppliers tab. */}
          <Route path="/whatsapp_messages" element={<Navigate to="/querysetv4" replace state={{ chatListAudience: "supplier" }} />} />
          <Route path="/suppliers" element={<Protected permission={PERMISSIONS.SUPPLIERS_VIEW}><Supplier /></Protected>} />
          <Route path="/enquiries-for-me" element={<EnquiriesPage />} />
          <Route path="/hrm" element={<Protected permission={PERMISSIONS.USERS_VIEW}><Hrm /></Protected>} />
          <Route path="/settings/access-management" element={<Navigate to="/settings/access/presets" replace />} />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/reports" element={<Protected permission={PERMISSIONS.REPORTS_VIEW}><Reports /></Protected>} />
          <Route path="/reports/overview" element={<Protected permission={PERMISSIONS.REPORTS_OVERVIEW_VIEW}><OverviewReport /></Protected>} />
          <Route path="/search_results" element={<Protected permission={PERMISSIONS.PRICE_SEARCH_VIEW}><PriceSearch /></Protected>} />
          <Route path="/price-search" element={<Protected permission={PERMISSIONS.PRICE_SEARCH_VIEW}><PriceSearch /></Protected>} />
          <Route path="/customers" element={<Protected permission={PERMISSIONS.CUSTOMERS_VIEW}><CustomerManagement /></Protected>} />
          <Route path="/ai_assistant" element={<Protected permission={PERMISSIONS.AI_ASSISTANT_VIEW}><AIAssistant /></Protected>} />
          <Route path="/views-preview" element={<Protected permission={PERMISSIONS.DEVELOPER_TOOLS_VIEW}><ViewsPreview /></Protected>} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      )}
    </Routes>
    </>
  );
};

function App() {
  const [globalState, setGlobalState] = useState({
    messages: [],
    queryset: [],
    approval_messages: [],
  });

  // Without this, dropping a file anywhere outside a component's own drop
  // zone (including onto a Modal portal, which lives outside any chat
  // panel's own onDrop area) falls through to the browser's default
  // behavior: navigating the whole tab away to display that file. Blocking
  // dragover/drop at the window level stops that everywhere, unconditionally
  // — local onDrop handlers still fire normally since this only prevents the
  // browser's own default action, not React's synthetic event dispatch.
  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  // Every notification/message toast in the app should be click-to-dismiss.
  // Wired once here rather than at each of the 90+ call sites — see
  // dismissibleNotifications.js for why a single DOM-level listener covers
  // both the static antd API and App.useApp()'s context-scoped instance.
  useEffect(() => {
    const onClick = (e) => handleDismissibleNotificationClick(e.target);
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <ConfigProvider theme={APP_BRAND_THEME}>
    <AppearanceProvider>
    <GlobalContext.Provider value={{ globalState, setGlobalState }}>
      <PopupTheme>
      <AntApp component={false} notification={ANT_APP_NOTIFICATION_CONFIG}>
        <AppPopupDismisser />
        <ConfigProvider theme={APP_CONTENT_THEME}>
        <AppTextRoot>
        <AuthProvider>
          <SocketProvider>
            <AccessProvider>
              <BusinessResolutionRecovery />
              <MediaAnalysisProvider>
              <InternalChatProvider>
                <WatiSocketProvider>
                  <Router>
                    <OperationsNotificationsProvider>
                      <AccountsNotificationsProvider>
                          <CanonicalMessagingProvider>
                          <CanonicalChatNotifications />
                          <QuarantineNotifications />
                          <CustomerProfileOverlayProvider>
                            <MailboxProvider>
                            <SourcingProvider>
                              <AutomationNotifications />
                            <Suspense fallback={<AppSplash />}>
                              <AuthenticatedApp />
                            </Suspense>
                            </SourcingProvider>
                            </MailboxProvider>
                          </CustomerProfileOverlayProvider>
                          </CanonicalMessagingProvider>
                      </AccountsNotificationsProvider>
                    </OperationsNotificationsProvider>
                  </Router>
                </WatiSocketProvider>
              </InternalChatProvider>
              </MediaAnalysisProvider>
            </AccessProvider>
          </SocketProvider>
        </AuthProvider>
        </AppTextRoot>
        </ConfigProvider>
      </AntApp>
      </PopupTheme>
    </GlobalContext.Provider>
    </AppearanceProvider>
    </ConfigProvider>
  );
}

export default App;
